import { useMemo, useState, useEffect } from 'react';
import { X, Download, Mail, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { reconciliationApi, type InvoiceDetail } from '@/api/reconciliation';
import { computeUnpaidDocumentsForEmail, type UnpaidDocumentForEmail } from '@shared/types';

interface Operation {
  date: string;
  description: string;
  debit: number;
  credit: number;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  actId?: string;
  actDate?: string;
  actReturnStatus?: string;
  paymentId?: string;
  documentNumber?: string;
  isPending?: boolean;
}

const ACT_RETURN_RETURNED = ['RETURNED', 'ARCHIVED', 'NON_RETURNABLE'];
const ACT_RETURN_LABELS: Record<string, string> = {
  NOT_RETURNED: 'Не вернули',
  CLIENT_DELIVERED: 'Отдан клиенту',
  CLIENT_RETURNED: 'Клиент вернул',
  RETURNED: 'Вернули',
  ARCHIVED: 'В архиве',
  NON_RETURNABLE: 'Невозвратный',
};

interface ReconciliationData {
  client?: {
    byActs?: boolean;
  };
  operations: Operation[];
  openingBalance: { debit: number; credit: number };
  turnover: { debit: number; credit: number };
  closingBalance: { debit: number; credit: number };
}

interface InvoiceWithCoverage {
  op: Operation;
  amount: number;
  paidAmount: number;
  remainingAmount: number;
  color: string;
  isPending?: boolean;
}

interface PaymentWithUsage {
  op: Operation;
  amount: number;
  usedAmount: number;
  unusedAmount: number;
  color: string;
}

interface PopupData {
  type: 'invoice' | 'payment';
  invoice?: InvoiceWithCoverage;
  payment?: PaymentWithUsage;
}

// Color palettes
const INVOICE_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
];

const PAYMENT_COLORS = [
  '#166534', '#15803d', '#16a34a', '#22c55e', '#4ade80',
  '#065f46', '#047857', '#059669', '#10b981', '#34d399',
];

export type UnpaidDocument = UnpaidDocumentForEmail;

interface BalanceVisualizationProps {
  data: ReconciliationData;
  signerId?: string;
  onDownloadUnpaid?: (unpaidDocs: UnpaidDocument[]) => void;
  onSendDebtEmail?: (unpaidDocs: UnpaidDocument[], totalDebt: number) => void;
  onPreviewDocument?: (doc: UnpaidDocument) => Promise<void>;
}

export function BalanceVisualization({ data, onDownloadUnpaid, onSendDebtEmail, onPreviewDocument }: BalanceVisualizationProps) {
  const [popup, setPopup] = useState<PopupData | null>(null);
  const [previewingDocId, setPreviewingDocId] = useState<string | null>(null);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch invoice detail when popup opens for an invoice
  useEffect(() => {
    if (popup?.type === 'invoice' && popup.invoice?.op.invoiceId) {
      setDetailLoading(true);
      setInvoiceDetail(null);
      reconciliationApi.getInvoiceDetail(popup.invoice.op.invoiceId)
        .then(setInvoiceDetail)
        .catch(() => setInvoiceDetail(null))
        .finally(() => setDetailLoading(false));
    } else {
      setInvoiceDetail(null);
    }
  }, [popup]);

  const handlePreview = async (doc: UnpaidDocument) => {
    if (!onPreviewDocument) return;
    setPreviewingDocId(doc.invoiceId);
    try {
      await onPreviewDocument(doc);
    } finally {
      setPreviewingDocId(null);
    }
  };

  const { invoices, payments, openingDebt, openingOverpay, totalInvoices, totalPayments, maxTotal } = useMemo(() => {
    const openingDebtAmount = data.openingBalance?.debit || 0;
    const openingOverpayAmount = data.openingBalance?.credit || 0;

    const invoiceOps = data.operations.filter(op => op.debit > 0);
    const paymentOps = data.operations.filter(op => op.credit > 0);

    const totalInv = openingDebtAmount + invoiceOps.reduce((sum, op) => sum + op.debit, 0);
    const totalPay = openingOverpayAmount + paymentOps.reduce((sum, op) => sum + op.credit, 0);

    // FIFO coverage calculation
    let availablePayment = openingOverpayAmount;

    let openingDebtPaid = 0;
    let openingDebtRemaining = openingDebtAmount;
    if (openingDebtAmount > 0 && availablePayment > 0) {
      const toPay = Math.min(availablePayment, openingDebtAmount);
      openingDebtPaid = toPay;
      openingDebtRemaining = openingDebtAmount - toPay;
      availablePayment -= toPay;
    }

    const remainingPayments = paymentOps.map(op => ({ ...op, remaining: op.credit }));

    for (const payment of remainingPayments) {
      if (openingDebtRemaining <= 0) break;
      if (payment.remaining <= 0) continue;
      const toPay = Math.min(payment.remaining, openingDebtRemaining);
      openingDebtPaid += toPay;
      openingDebtRemaining -= toPay;
      payment.remaining -= toPay;
    }

    const invoicesWithCoverage: InvoiceWithCoverage[] = invoiceOps.map((op, idx) => {
      let paidAmount = 0;
      const invoiceAmount = op.debit;

      if (availablePayment > 0) {
        const toPay = Math.min(availablePayment, invoiceAmount);
        paidAmount += toPay;
        availablePayment -= toPay;
      }

      for (const payment of remainingPayments) {
        if (payment.remaining <= 0) continue;
        if (paidAmount >= invoiceAmount) break;
        const toPay = Math.min(payment.remaining, invoiceAmount - paidAmount);
        paidAmount += toPay;
        payment.remaining -= toPay;
      }

      return {
        op,
        amount: invoiceAmount,
        paidAmount,
        remainingAmount: invoiceAmount - paidAmount,
        color: INVOICE_COLORS[idx % INVOICE_COLORS.length],
        isPending: op.isPending,
      };
    });

    // Payment usage calculation
    let usedFromOverpay = 0;
    let remainingOverpay = openingOverpayAmount;

    if (openingDebtAmount > 0 && remainingOverpay > 0) {
      const toUse = Math.min(remainingOverpay, openingDebtAmount);
      usedFromOverpay += toUse;
      remainingOverpay -= toUse;
    }

    const invoicesCopyForOverpay = invoiceOps.map(op => ({ ...op, remaining: op.debit }));
    for (const invoice of invoicesCopyForOverpay) {
      if (remainingOverpay <= 0) break;
      if (invoice.remaining <= 0) continue;
      const toUse = Math.min(invoice.remaining, remainingOverpay);
      usedFromOverpay += toUse;
      remainingOverpay -= toUse;
      invoice.remaining -= toUse;
    }

    const invoicesCopy = invoicesCopyForOverpay;
    let debtRemaining = openingDebtAmount - Math.min(openingDebtAmount, openingOverpayAmount);

    const paymentsWithUsage: PaymentWithUsage[] = paymentOps.map((op, idx) => {
      let usedAmount = 0;
      const paymentAmount = op.credit;

      if (debtRemaining > 0) {
        const toUse = Math.min(debtRemaining, paymentAmount);
        usedAmount += toUse;
        debtRemaining -= toUse;
      }

      for (const invoice of invoicesCopy) {
        if (invoice.remaining <= 0) continue;
        if (usedAmount >= paymentAmount) break;
        const toUse = Math.min(invoice.remaining, paymentAmount - usedAmount);
        usedAmount += toUse;
        invoice.remaining -= toUse;
      }

      return {
        op,
        amount: paymentAmount,
        usedAmount,
        unusedAmount: paymentAmount - usedAmount,
        color: PAYMENT_COLORS[idx % PAYMENT_COLORS.length],
      };
    });

    const max = Math.max(totalInv, totalPay);

    return {
      invoices: invoicesWithCoverage,
      payments: paymentsWithUsage,
      openingDebt: {
        amount: openingDebtAmount,
        paidAmount: openingDebtPaid,
        remainingAmount: openingDebtRemaining,
      },
      openingOverpay: {
        amount: openingOverpayAmount,
        usedAmount: usedFromOverpay,
        unusedAmount: openingOverpayAmount - usedFromOverpay,
      },
      totalInvoices: totalInv,
      totalPayments: totalPay,
      maxTotal: max,
    };
  }, [data.operations, data.openingBalance]);

  const COLUMN_HEIGHT = 280;
  const MIN_SEGMENT_HEIGHT = 20;

  const balanceLevel = useMemo(() => {
    if (maxTotal === 0) return 0;
    const minTotal = Math.min(totalInvoices, totalPayments);
    return (minTotal / maxTotal) * COLUMN_HEIGHT;
  }, [totalInvoices, totalPayments, maxTotal]);

  // Список неоплаченных документов для кнопок скачать/выслать
  const { unpaidDocuments, totalDebt } = useMemo(() => {
    return computeUnpaidDocumentsForEmail(data);
  }, [data]);

  const formatMoney = (amount: number) => {
    const hasDecimals = amount % 1 !== 0;
    return amount.toLocaleString('ru-RU', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    });
  };

  const formatDateFull = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Format: 06.25 (month.year)
  const formatMonthYear = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}.${year}`;
  };

  // Format: 31.12.25 (day.month.year)
  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  };

  if (data.operations.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="text-sm font-medium mb-4">Визуализация баланса</h4>

        {/* Balance summary */}
        <div className="text-center mb-4">
          {totalPayments > totalInvoices ? (
            <div className="text-green-600 font-semibold">
              Переплата: {formatMoney(totalPayments - totalInvoices)} BYN
            </div>
          ) : totalInvoices > totalPayments ? (
            <div className="text-red-600 font-semibold">
              Долг: {formatMoney(totalInvoices - totalPayments)} BYN
            </div>
          ) : (
            <div className="text-slate-600 font-semibold">
              Баланс: 0 BYN
            </div>
          )}
        </div>

        {/* Unpaid invoices list */}
        {unpaidDocuments.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">Неоплаченные счета:</div>
            <div className="space-y-1.5 text-sm">
              {unpaidDocuments.map((doc, idx) => {
                const paidAmount = doc.amount - doc.remainingAmount;
                const isLoading = previewingDocId === doc.invoiceId;

                return (
                  <div key={`${doc.isPending ? 'invoice' : 'act'}:${doc.invoiceId}:${doc.actId || idx}`} className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    {onPreviewDocument && (
                      <button
                        onClick={() => handlePreview(doc)}
                        disabled={isLoading}
                        className="p-1 rounded hover:bg-amber-200 dark:hover:bg-amber-800 disabled:opacity-50"
                        title="Просмотр"
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    <span className="flex-1">
                      {doc.isPending ? 'Счёт' : 'Акт'} №{doc.invoiceNumber} от {formatDateFull(doc.date)}
                    </span>
                    <span className="font-medium whitespace-nowrap">
                      {paidAmount > 0
                        ? <><span className="text-amber-600 dark:text-amber-400">доплатить</span> {formatMoney(doc.remainingAmount)}</>
                        : <>{formatMoney(doc.amount)} BYN</>
                      }
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Buttons */}
            {(onDownloadUnpaid || onSendDebtEmail) && (
              <div className="flex gap-2 mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
                {onDownloadUnpaid && (
                  <Button
                    variant="download"
                    size="sm"
                    className="flex-1"
                    onClick={() => onDownloadUnpaid(unpaidDocuments)}
                  >
                    <Download className="h-4 w-4" />
                    Скачать
                  </Button>
                )}
                {onSendDebtEmail && (
                  <Button
                    variant="email"
                    size="sm"
                    className="flex-1"
                    onClick={() => onSendDebtEmail(unpaidDocuments, totalDebt)}
                  >
                    <Mail className="h-4 w-4" />
                    Выслать
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Two columns */}
        <div className="flex gap-2">
          {/* Invoices column */}
          <div className="flex-1">
            <div className="text-xs text-center text-muted-foreground mb-1">
              Акты: {formatMoney(totalInvoices)}
            </div>
            <div
              className="relative bg-muted rounded-t border overflow-hidden"
              style={{ height: COLUMN_HEIGHT }}
            >
              <div className="absolute bottom-0 left-0 right-0 flex flex-col-reverse">
                {/* Opening debt */}
                {openingDebt.amount > 0 && (() => {
                  const height = maxTotal > 0 ? (openingDebt.amount / maxTotal) * COLUMN_HEIGHT : 0;
                  return (
                    <div
                      className="w-full relative flex items-center justify-center"
                      style={{
                        height: `${height}px`,
                        backgroundColor: '#7c3aed',
                        minHeight: height > 0 ? '2px' : 0,
                      }}
                    >
                      {height >= MIN_SEGMENT_HEIGHT && (
                        <div className="text-white text-[10px] text-center truncate px-1">
                          Вх.{formatMoney(openingDebt.amount)}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {invoices.map((inv, idx) => {
                  const height = maxTotal > 0 ? (inv.amount / maxTotal) * COLUMN_HEIGHT : 0;
                  const isPaid = inv.remainingAmount <= 0;

                  return (
                    <div
                      key={inv.op.invoiceId || idx}
                      className={cn(
                        "w-full cursor-pointer relative flex items-center justify-center",
                        inv.isPending && "border border-dashed border-white/50"
                      )}
                      style={{
                        height: `${height}px`,
                        backgroundColor: inv.color,
                        minHeight: height > 0 ? '2px' : 0,
                        ...(inv.isPending && {
                          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.15) 3px, rgba(255,255,255,0.15) 6px)`,
                        }),
                      }}
                      onClick={() => setPopup({ type: 'invoice', invoice: inv })}
                    >
                      {height >= MIN_SEGMENT_HEIGHT && (
                        <div className="text-white text-[10px] text-center truncate px-0.5 leading-tight">
                          #{inv.op.invoiceNumber} - {formatMonthYear(inv.op.date)} - {formatMoney(inv.amount)}р{' '}{isPaid ? <span className="text-green-200">$</span> : <span className="text-red-200 line-through">$</span>}{!inv.isPending && inv.op.actReturnStatus && (
                            ACT_RETURN_RETURNED.includes(inv.op.actReturnStatus)
                              ? <span className="text-green-200">✓</span>
                              : <span className="text-red-200">✗</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Balance line */}
              <div
                className="absolute left-0 right-0 border-t-2 border-dashed border-blue-600 z-10"
                style={{ bottom: balanceLevel }}
              />
            </div>
          </div>

          {/* Payments column */}
          <div className="flex-1">
            <div className="text-xs text-center text-muted-foreground mb-1">
              Платежи: {formatMoney(totalPayments)}
            </div>
            <div
              className="relative bg-muted rounded-t border overflow-hidden"
              style={{ height: COLUMN_HEIGHT }}
            >
              <div className="absolute bottom-0 left-0 right-0 flex flex-col-reverse">
                {/* Opening overpay */}
                {openingOverpay.amount > 0 && (() => {
                  const height = maxTotal > 0 ? (openingOverpay.amount / maxTotal) * COLUMN_HEIGHT : 0;
                  return (
                    <div
                      className="w-full relative flex items-center justify-center"
                      style={{
                        height: `${height}px`,
                        backgroundColor: '#7c3aed',
                        minHeight: height > 0 ? '2px' : 0,
                      }}
                    >
                      {height >= MIN_SEGMENT_HEIGHT && (
                        <div className="text-white text-[10px] text-center truncate px-1">
                          Вх.{formatMoney(openingOverpay.amount)}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {payments.map((pay, idx) => {
                  const height = maxTotal > 0 ? (pay.amount / maxTotal) * COLUMN_HEIGHT : 0;
                  const isFullyUsed = pay.unusedAmount <= 0;

                  return (
                    <div
                      key={pay.op.paymentId || idx}
                      className="w-full cursor-pointer relative flex items-center justify-center"
                      style={{
                        height: `${height}px`,
                        backgroundColor: pay.color,
                        minHeight: height > 0 ? '2px' : 0,
                      }}
                      onClick={() => setPopup({ type: 'payment', payment: pay })}
                    >
                      {height >= MIN_SEGMENT_HEIGHT && (
                        <div className="text-white text-[10px] text-center truncate px-0.5 leading-tight">
                          {formatShortDate(pay.op.date)} - {formatMoney(pay.amount)}р{!isFullyUsed ? ' +' : ''}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Balance line */}
              <div
                className="absolute left-0 right-0 border-t-2 border-dashed border-blue-600 z-10"
                style={{ bottom: balanceLevel }}
              />
            </div>
          </div>
        </div>

        {/* Detail popup */}
        {popup && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setPopup(null)}
          >
            <div
              className="bg-card text-card-foreground rounded-lg shadow-xl p-4 w-full max-w-sm max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-3">
                <h5 className="font-semibold">
                  {popup.type === 'invoice' && popup.invoice && (
                    popup.invoice.isPending
                      ? <>Счёт №{popup.invoice.op.invoiceNumber} от {formatDateFull(popup.invoice.op.date)}</>
                      : <>Акт №{popup.invoice.op.invoiceNumber} от {formatDateFull(popup.invoice.op.date)}</>
                  )}
                  {popup.type === 'payment' && popup.payment && (
                    <>Платёж ПП №{popup.payment.op.documentNumber?.replace(/^0+/, '') || '—'} от {formatDateFull(popup.payment.op.date)}</>
                  )}
                </h5>
                <button onClick={() => setPopup(null)} className="text-muted-foreground flex-shrink-0 ml-2">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {popup.type === 'invoice' && popup.invoice && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Сумма:</span>
                    <span className="font-medium">{formatMoney(popup.invoice.amount)} BYN</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Оплачено:</span>
                    <span className="text-green-600">{formatMoney(popup.invoice.paidAmount)} BYN</span>
                  </div>
                  {popup.invoice.remainingAmount > 0 && (
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">К оплате:</span>
                      <span className="font-bold text-red-600">{formatMoney(popup.invoice.remainingAmount)} BYN</span>
                    </div>
                  )}
                  {popup.invoice.remainingAmount <= 0 && (
                    <div className="text-center text-green-600 font-medium pt-2 border-t">
                      ✓ Полностью оплачен
                    </div>
                  )}
                  {/* Progress bar */}
                  <div className="mt-2">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${(popup.invoice.paidAmount / popup.invoice.amount) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground text-center mt-1">
                      {Math.round((popup.invoice.paidAmount / popup.invoice.amount) * 100)}% оплачено
                    </div>
                  </div>

                  {/* Act return status */}
                  {!popup.invoice.isPending && popup.invoice.op.actReturnStatus && (
                    <div className="flex justify-between pt-2 border-t mt-2">
                      <span className="text-muted-foreground">Документ:</span>
                      <span className={cn("font-medium", ACT_RETURN_RETURNED.includes(popup.invoice.op.actReturnStatus) ? "text-green-600" : "text-red-600")}>
                        {ACT_RETURN_LABELS[popup.invoice.op.actReturnStatus] || popup.invoice.op.actReturnStatus}
                      </span>
                    </div>
                  )}

                  {/* Invoice breakdown */}
                  {detailLoading && (
                    <div className="flex items-center justify-center pt-2 border-t mt-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {invoiceDetail && (
                    <div className="pt-2 border-t mt-2 space-y-1.5">
                      <div className="text-xs font-medium text-muted-foreground">Состав счёта</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground">
                            <th className="text-left font-medium pb-0.5"></th>
                            <th className="text-right font-medium pb-0.5">Кол.</th>
                            <th className="text-right font-medium pb-0.5">Цена</th>
                            <th className="text-right font-medium pb-0.5">Итого</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceDetail.pcCount > 0 && (
                            <tr>
                              <td>ПК</td>
                              <td className="text-right">{invoiceDetail.pcCount}</td>
                              <td className="text-right">{formatMoney(invoiceDetail.pricePerPc)}</td>
                              <td className="text-right">{formatMoney(invoiceDetail.pcTotal)}</td>
                            </tr>
                          )}
                          {invoiceDetail.serverCount > 0 && (
                            <tr>
                              <td>Серверы</td>
                              <td className="text-right">{invoiceDetail.serverCount}</td>
                              <td className="text-right">{formatMoney(invoiceDetail.pricePerServer)}</td>
                              <td className="text-right">{formatMoney(invoiceDetail.serverTotal)}</td>
                            </tr>
                          )}
                          {invoiceDetail.services.map((svc, i) => (
                            <tr key={i}>
                              <td colSpan={3} className="truncate max-w-[180px]">{svc.name}</td>
                              <td className="text-right">{formatMoney(svc.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Offices: main + additional */}
                      <div className="text-xs text-muted-foreground pt-1">
                        <span>
                          Осн.{invoiceDetail.employee ? ` (${invoiceDetail.employee.lastName})` : ''}: {invoiceDetail.accountedPCs} КК
                        </span>
                        {invoiceDetail.offices.map((o, i) => (
                          <span key={i}>
                            {' · '}{o.office?.name || '—'}
                            {o.employee ? ` (${o.employee.lastName})` : ''}
                            : {o.accountedPCs} КК
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {popup.type === 'payment' && popup.payment && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Сумма:</span>
                    <span className="font-medium">{formatMoney(popup.payment.amount)} BYN</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Использовано:</span>
                    <span>{formatMoney(popup.payment.usedAmount)} BYN</span>
                  </div>
                  {popup.payment.unusedAmount > 0 && (
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Остаток (аванс):</span>
                      <span className="font-bold text-amber-600">{formatMoney(popup.payment.unusedAmount)} BYN</span>
                    </div>
                  )}
                  {popup.payment.unusedAmount <= 0 && (
                    <div className="text-center text-slate-600 font-medium pt-2 border-t">
                      Полностью распределён
                    </div>
                  )}
                  {/* Progress bar */}
                  <div className="mt-2">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${(popup.payment.usedAmount / popup.payment.amount) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground text-center mt-1">
                      {Math.round((popup.payment.usedAmount / popup.payment.amount) * 100)}% использовано
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
